import React, { useRef, useState, DragEvent } from "react";
import { Button } from "@/components/ui/button";
import { Upload, AlertCircle, CheckCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import * as XLSX from 'xlsx';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ImportUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportSuccess: () => void;
}

interface ExcelUserData {
  company_name: string;
  full_name: string;
  email: string;
  role: string;
  is_unit_head: boolean;
  is_admin: boolean;
  access_administration: boolean;
  access_hr: boolean;
  access_sales: boolean;
  access_marketing: boolean;
  access_finance: boolean;
  access_projects: boolean;
  access_legal: boolean;
  access_performance: boolean;
  enable_login: boolean;
}

export const ImportUsersDialog = ({ open, onOpenChange, onImportSuccess }: ImportUsersDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [detailedErrors, setDetailedErrors] = useState<string[]>([]);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successCount, setSuccessCount] = useState(0);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const validateExcelData = (data: any[]): ExcelUserData[] => {
    if (!data || data.length === 0) {
      throw new Error("No data found in the Excel file");
    }

    const requiredFields = ['company_name', 'full_name', 'email', 'role'];
    const firstRow = data[0];
    
    const missingFields = requiredFields.filter(field => !(field in firstRow));
    if (missingFields.length > 0) {
      throw new Error(`Missing required columns: ${missingFields.join(', ')}`);
    }

    return data.map((row, index) => {
      // Validate required fields
      if (!row.email || !row.full_name) {
        throw new Error(`Row ${index + 1}: Missing required data (email or full_name)`);
      }

      // Parse boolean values (handle various formats)
      const parseBoolean = (value: any): boolean => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
          const normalized = value.toLowerCase().trim();
          return normalized === 'true' || normalized === '1' || normalized === 'yes';
        }
        if (typeof value === 'number') return value === 1;
        return false;
      };

      return {
        company_name: row.company_name || '',
        full_name: row.full_name || '',
        email: row.email || '',
        role: row.role || 'Team member',
        is_unit_head: parseBoolean(row.is_unit_head),
        is_admin: parseBoolean(row.is_admin),
        access_administration: parseBoolean(row.access_administration),
        access_hr: parseBoolean(row.access_hr),
        access_sales: parseBoolean(row.access_sales),
        access_marketing: parseBoolean(row.access_marketing),
        access_finance: parseBoolean(row.access_finance),
        access_projects: parseBoolean(row.access_projects),
        access_legal: parseBoolean(row.access_legal),
        access_performance: parseBoolean(row.access_performance),
        enable_login: parseBoolean(row.enable_login)
      };
    });
  };

  const processFile = async (file: File) => {
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      setUploadStatus('error');
      setStatusMessage('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }

    setIsUploading(true);
    setUploadStatus('idle');

    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const validatedData = validateExcelData(jsonData);

          // Get current user ID
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            throw new Error('User not authenticated');
          }

          // Call the edge function to import users
          const { data: response, error } = await supabase.functions.invoke('import-users', {
            body: { 
              users: validatedData,
              admin_user_id: user.id
            }
          });

          if (error) {
            throw error;
          }

          const { success, created_count, errors } = response;

          if (success && created_count > 0) {
            setUploadStatus('success');
            setStatusMessage(`Successfully imported ${created_count} user(s) with default password "Ingant@777"`);
            setDetailedErrors(errors || []);
            setSuccessCount(created_count);
            setShowSuccessAlert(true);
            toast.success(`Successfully imported ${created_count} user(s)`);
            onImportSuccess();
            
            // Close dialog after showing success alert
            onOpenChange(false);
            setUploadStatus('idle');
            setStatusMessage('');
            setDetailedErrors([]);
          } else {
            setUploadStatus('error');
            const errorDetails = errors?.length > 0 ? `${errors.length} error(s) occurred` : 'Unknown import error';
            setStatusMessage(`Import failed: ${errorDetails}`);
            setDetailedErrors(errors || []);
            toast.error('Import failed - see details in dialog');
          }

        } catch (error: any) {
          console.error('Import error:', error);
          setUploadStatus('error');
          setStatusMessage(error.message || 'Failed to import users');
          setDetailedErrors([]);
          toast.error(error.message || 'Failed to import users');
        } finally {
          setIsUploading(false);
        }
      };

      reader.readAsBinaryString(file);
    } catch (error: any) {
      console.error('File processing error:', error);
      setUploadStatus('error');
      setStatusMessage(error.message || 'Failed to process file');
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      onOpenChange(false);
      setUploadStatus('idle');
      setStatusMessage('');
      setDetailedErrors([]);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="bg-secondary text-white border-primary/20">
          <DialogHeader>
            <DialogTitle>Import Users</DialogTitle>
            <DialogDescription className="text-primary-light">
              Upload an Excel file (.xlsx or .xls) to import multiple users at once.
            </DialogDescription>
          </DialogHeader>
        
        {uploadStatus !== 'idle' && (
          <Alert className={`${uploadStatus === 'success' ? 'border-green-500 bg-green-500/10' : 'border-red-500 bg-red-500/10'}`}>
            {uploadStatus === 'success' ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-500" />
            )}
            <AlertDescription className={uploadStatus === 'success' ? 'text-green-400' : 'text-red-400'}>
              {statusMessage}
            </AlertDescription>
          </Alert>
        )}

        {detailedErrors.length > 0 && (
          <Alert className="border-yellow-500 bg-yellow-500/10 max-h-40 overflow-y-auto">
            <AlertCircle className="h-4 w-4 text-yellow-500" />
            <AlertDescription className="text-yellow-400">
              <div className="font-medium mb-2">Issues encountered:</div>
              <div className="space-y-1 text-sm">
                {detailedErrors.map((error, index) => (
                  <div key={index} className="text-yellow-300">• {error}</div>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragging 
                ? 'border-primary bg-primary/10' 
                : 'border-primary/30 hover:border-primary/50 hover:bg-primary/5'
            } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`} 
            onClick={!isUploading ? handleFileSelect : undefined}
            onDragEnter={handleDragEnter} 
            onDragOver={handleDragOver} 
            onDragLeave={handleDragLeave} 
            onDrop={handleDrop}
          >
            <input 
              ref={fileInputRef} 
              type="file" 
              accept=".xlsx,.xls" 
              className="hidden" 
              onChange={handleFileInputChange}
              disabled={isUploading}
            />
            <Upload className="h-10 w-10 mx-auto mb-3 text-primary-light" />
            <p className="text-white font-medium">
              {isUploading ? 'Processing...' : 'Click to browse or drag and drop'}
            </p>
            <p className="text-primary-light text-sm mt-1">
              Supports Excel files (.xlsx, .xls)
            </p>
          </div>

        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose} 
            className="border-primary/20 text-primary-light bg-transparent hover:bg-primary/10"
            disabled={isUploading}
          >
            {isUploading ? 'Processing...' : 'Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={showSuccessAlert} onOpenChange={setShowSuccessAlert}>
      <AlertDialogContent className="bg-secondary text-white border-primary/20">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Import Successful</AlertDialogTitle>
          <AlertDialogDescription className="text-primary-light">
            Successfully imported {successCount} user{successCount > 1 ? 's' : ''} to the system.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction 
            onClick={() => setShowSuccessAlert(false)}
            className="bg-primary text-white hover:bg-primary/90"
          >
            OK
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};